/**
 * spanFragmentNormalizer.ts
 *
 * Repara texto fragmentado producido por LibreOffice al dividir palabras
 * en múltiples elementos <text:span> dentro de documentos ODT.
 *
 * PROBLEMA:
 *   LibreOffice genera XML como:
 *     <text:span>Almacenamiento </text:span><text:span>d</text:span>
 *     <text:span> e </text:span><text:span>p</text:span>
 *     <text:span> roductos</text:span>
 *   Al concatenar textContent se obtiene:
 *     "Almacenamiento d e p roductos p eligrosos"
 *   En vez de:
 *     "Almacenamiento de productos peligrosos"
 *
 * ESTRATEGIA:
 *   0. Prepaso: si TODOS los tokens son 1 char → unir todo ("D E P O S I T O" → "DEPOSITO")
 *   1. Detectar fragmentos de 1 letra rodeados de espacios (" d ", " e ")
 *   2. Acumular letras sueltas no legítimas; flush al formar palabra autónoma
 *   3. Pegar acumulador restante al siguiente token multiletter si parece continuación
 *   4. Fase 2: reparar prefijos cortos separados ("pr oductos" → "productos")
 *
 * ORDEN EN PIPELINE:
 *   rawText → normalizeSpanFragments() → deconcatenateText() → nombre limpio
 *
 * @module lib/spanFragmentNormalizer
 * @version 1.1.0
 */

// ============================================================================
// TIPOS
// ============================================================================

export interface SpanFragmentResult {
  /** Texto corregido */
  corrected: string;
  /** true si se aplicó alguna corrección */
  wasModified: boolean;
  /** Número de fragmentos reparados */
  fragmentsRepaired: number;
}

// ============================================================================
// CONSTANTES
// ============================================================================

/**
 * Palabras españolas cortas que son legítimas y NO deben fusionarse.
 */
const STANDALONE_SHORT_WORDS = new Set([
  // 1 letra
  'a', 'y', 'o', 'e', 'u',
  // 2 letras
  'de', 'el', 'en', 'al', 'la', 'lo', 'un', 'no', 'se', 'me', 'te',
  'le', 'si', 'ya', 'ni', 'mi', 'su', 'ha', 'he', 'es',
  // 3 letras
  'del', 'las', 'los', 'por', 'con', 'sin', 'una', 'uno', 'que',
  'son', 'más', 'mas', 'muy', 'fue', 'ser', 'hay', 'dos', 'sur',
]);

/** Letras que pueden ser palabras de 1 letra en español */
const LEGITIMATE_SINGLE = new Set(['a', 'y', 'o', 'e', 'u']);

// ============================================================================
// FUNCIONES AUXILIARES
// ============================================================================

function isLegitSingle(ch: string): boolean {
  return LEGITIMATE_SINGLE.has(ch.toLowerCase());
}

function isSingleAlpha(token: string): boolean {
  return token.length === 1 && /^[a-záéíóúñA-ZÁÉÍÓÚÑ]$/.test(token);
}

/**
 * ¿El fragmento corto + largo parecen una palabra partida?
 * Ej: "p" + "roductos" → sí, "de" + "Salud" → no (empieza mayúscula)
 */
function looksLikeFragment(shortPart: string, longPart: string): boolean {
  if (!longPart || /^[A-ZÁÉÍÓÚÑ]/.test(longPart)) return false;
  if (STANDALONE_SHORT_WORDS.has(longPart.toLowerCase())) return false;
  return longPart.length >= 3;
}

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

export function normalizeSpanFragments(input: string): SpanFragmentResult {
  if (!input || typeof input !== 'string') {
    return { corrected: input ?? '', wasModified: false, fragmentsRepaired: 0 };
  }

  const text = input.replace(/\s+/g, ' ').trim();

  // Early exit: detección rápida de fragmentación
  const hasIsolatedLetters = /\s[a-záéíóúñA-ZÁÉÍÓÚÑ]\s/.test(text);
  const hasShortPrefix = /(?:^|\s)[A-Za-záéíóúñÁÉÍÓÚÑ]{1,3}\s[a-záéíóúñ]{3,}/.test(text);
  if (!hasIsolatedLetters && !hasShortPrefix) {
    return { corrected: text, wasModified: text !== input, fragmentsRepaired: 0 };
  }

  const tokens = text.split(' ').filter(t => t);
  let fragmentsRepaired = 0;

  // -------------------------------------------------------------------
  // PREPASO: Si TODOS los tokens son chars sueltos → unir todo.
  // Caso: "D E P O S I T O" → "DEPOSITO"
  // -------------------------------------------------------------------
  const allSingleChars = tokens.every(t => isSingleAlpha(t));
  if (allSingleChars && tokens.length > 1) {
    const joined = tokens.join('');
    return {
      corrected: joined,
      wasModified: true,
      fragmentsRepaired: tokens.length,
    };
  }

  // -------------------------------------------------------------------
  // FASE 1: Acumular letras sueltas, flush al formar palabra autónoma
  // o al encontrar token multiletter.
  //
  // "d e p roductos" → acc "d", acc "de" (flush), acc "p", merge "productos"
  // "C e ntro" → acc "C", acc "Ce", merge "Centro"
  // -------------------------------------------------------------------
  const merged: string[] = [];
  let acc = '';

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    if (isSingleAlpha(token)) {
      const nextToken = tokens[i + 1] || '';

      // Caso 1: Legítima (a/y/o/e/u) SIN acumulador previo
      if (isLegitSingle(token) && !acc) {
        // ¿La siguiente también es suelta no legítima? → inicio de fragmentación
        if (isSingleAlpha(nextToken) && !isLegitSingle(nextToken)) {
          acc = token;
          fragmentsRepaired++;
        } else {
          merged.push(token);
        }
      } else {
        // Acumular (no legítima, o hay acumulador en curso)
        acc += token;
        fragmentsRepaired++;

        // ¿Formamos palabra autónoma? → flush inmediato
        if (STANDALONE_SHORT_WORDS.has(acc.toLowerCase())) {
          merged.push(acc);
          acc = '';
        }
      }
    } else {
      // Token multiletter: resolver acumulador pendiente
      if (acc) {
        if (looksLikeFragment(acc, token)) {
          // "p" + "roductos" → "productos"
          merged.push(acc + token);
          acc = '';
          fragmentsRepaired++;
          continue;
        }
        // No es continuación → emitir acumulador como palabra independiente
        merged.push(acc);
        acc = '';
      }
      merged.push(token);
    }
  }
  if (acc) merged.push(acc);

  // -------------------------------------------------------------------
  // FASE 2: Prefijos cortos separados (2-3 letras + minúscula ≥3 chars)
  // "Co legio" → "Colegio", "Dep ósito" → "Depósito"
  // -------------------------------------------------------------------
  const final: string[] = [];

  for (let i = 0; i < merged.length; i++) {
    const cur = merged[i];
    const nxt = merged[i + 1];

    if (
      nxt &&
      cur.length >= 1 && cur.length <= 3 &&
      !STANDALONE_SHORT_WORDS.has(cur.toLowerCase()) &&
      /^[a-záéíóúñ]/.test(nxt) &&
      nxt.length >= 3 &&
      !STANDALONE_SHORT_WORDS.has(nxt.toLowerCase())
    ) {
      final.push(cur + nxt);
      fragmentsRepaired++;
      i++;
    } else {
      final.push(cur);
    }
  }

  const result = final.join(' ');
  const wasModified = result !== text;

  return {
    corrected: result,
    wasModified,
    fragmentsRepaired: wasModified ? fragmentsRepaired : 0,
  };
}
